import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, KeyRound, CircleX } from 'lucide-react';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/store/useAuthStore';
import { PasswordRequirements } from '@/components/PasswordRequirements';
import { isPasswordValid, passwordsMatch } from '@/lib/passwordPolicy';
import { toast } from 'sonner';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const applySession = useAuthStore((s) => s.applySession);
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [processing, setProcessing] = useState(false);

  const ready =
    token.length > 0 &&
    isPasswordValid(password) &&
    passwordsMatch(password, confirmPassword);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setProcessing(true);
    try {
      const { data } = await authApi.resetPassword(token, password, confirmPassword);
      applySession(
        data.access_token,
        data.user as { name: string; email: string; role?: 'user' | 'admin' },
      );
      toast.success('Mot de passe mis à jour');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data
          ?.message?.toString() || 'Lien invalide ou expiré.',
      );
    } finally {
      setProcessing(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-app flex flex-col items-center justify-center p-6 text-center">
        <div className="w-full max-w-md bg-overlay-1 border border-line rounded-2xl p-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
            <CircleX className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-xl font-bold text-fg mb-2">Lien invalide</h1>
          <p className="text-sm text-fg-subtle mb-6">
            Le lien de réinitialisation est incomplet. Refaites une demande depuis la connexion.
          </p>
          <Link
            to="/dashboard"
            className="inline-flex items-center justify-center px-5 py-3 bg-overlay-2 border border-line text-fg rounded-xl text-sm font-semibold"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md bg-overlay-1 border border-line rounded-2xl p-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
            <KeyRound className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-fg mb-2">Nouveau mot de passe</h1>
          <p className="text-sm text-fg-subtle">Choisissez un nouveau mot de passe pour votre compte.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-faint" />
            <input
              type="password"
              placeholder="Nouveau mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full pl-10 pr-4 py-3 bg-app border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-sm"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-faint" />
            <input
              type="password"
              placeholder="Confirmer le mot de passe"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full pl-10 pr-4 py-3 bg-app border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-sm"
            />
          </div>

          <PasswordRequirements password={password} confirmPassword={confirmPassword} showMatch />

          <button
            type="submit"
            disabled={processing || !ready}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-60"
          >
            {processing ? 'Mise à jour…' : 'Réinitialiser mon mot de passe'}
          </button>
        </form>

        <div className="text-center mt-4">
          <Link to="/dashboard" className="text-xs text-fg-faint hover:text-fg-subtle transition-colors">
            Annuler
          </Link>
        </div>
      </div>
    </div>
  );
}
